import { Slot } from '@radix-ui/react-slot';
import React, { createContext, useContext, forwardRef, useId } from 'react';
import {
  Controller,
  FormProvider,
  useFormContext,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import {
  GAP_SCALE,
  TYPE_SCALE,
  FONT_WEIGHTS,
  COLOR_SCALE,
  RADIUS_SCALE,
  SIZE_SCALE,
  INTERACTIVE_STATES,
} from '@/components/design-system/tokens';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';

const Form = FormProvider;

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ ...props }: ControllerProps<TFieldValues, TName>) {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
}

const useFormField = () => {
  const fieldContext = useContext(FormFieldContext);
  const itemContext = useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const fieldState = getFieldState(fieldContext.name, formState);
  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

interface FormItemContextValue {
  id: string;
}

const FormItemContext = createContext<FormItemContextValue>({ id: '' });

const FormItem = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const id = useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn(`flex flex-col ${GAP_SCALE['2']}`, className)} {...props} />
      </FormItemContext.Provider>
    );
  }
);
FormItem.displayName = 'FormItem';

const FormLabel = forwardRef<
  React.ElementRef<typeof Label>,
  React.ComponentPropsWithoutRef<typeof Label>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      className={cn(error && COLOR_SCALE.destructive, className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = 'FormLabel';

interface FormControlProps extends React.ComponentPropsWithoutRef<typeof Slot> {
  showSuccess?: boolean;
}

const FormControl = forwardRef<React.ElementRef<typeof Slot>, FormControlProps>(
  ({ className, showSuccess = false, ...props }, ref) => {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

    return (
      <Slot
        ref={ref}
        id={formItemId}
        aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
        aria-invalid={!!error}
        className={cn(
          // Add success state styling if enabled and no error
          showSuccess && !error && `border border-green-500/50 bg-green-50/20`,
          // Error state styling
          error && `border border-red-500/50 bg-red-50/20`,
          // Focus transitions
          `transition-all duration-200`,
          className
        )}
        {...props}
      />
    );
  }
);
FormControl.displayName = 'FormControl';

const FormDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn(`${TYPE_SCALE['2xs']} ${COLOR_SCALE['foreground-muted']}`, className)}
      {...props}
    />
  );
});
FormDescription.displayName = 'FormDescription';

interface FormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
  variant?: 'error' | 'warning' | 'success' | 'info';
  showIcon?: boolean;
}

const FormMessage = forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ className, children, variant = 'error', showIcon = true, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error.message) : children;

    if (!body) {
      return null;
    }

    const variants = {
      error: {
        text: COLOR_SCALE.destructive,
        bg: COLOR_SCALE['destructive-bg'],
        icon: '⚠️',
      },
      warning: {
        text: COLOR_SCALE.warning,
        bg: COLOR_SCALE['warning-bg'],
        icon: '⚡',
      },
      success: {
        text: COLOR_SCALE.success,
        bg: COLOR_SCALE['success-bg'],
        icon: '✓',
      },
      info: {
        text: COLOR_SCALE.info,
        bg: COLOR_SCALE['info-bg'],
        icon: 'ℹ️',
      },
    };

    const style = variants[variant];

    return (
      <div
        ref={ref}
        id={formMessageId}
        className={cn(
          `flex items-start gap-2 ${TYPE_SCALE['2xs']} ${FONT_WEIGHTS.medium} ${RADIUS_SCALE.md} ${SIZE_SCALE['2']} ${INTERACTIVE_STATES.transitions.all} animate-in slide-in-from-top-1 duration-200`,
          style.bg,
          className
        )}
        {...props}
      >
        {showIcon && (
          <span className="flex-shrink-0 mt-0.5 text-xs" aria-hidden="true">
            {style.icon}
          </span>
        )}
        <span className={style.text}>{body}</span>
      </div>
    );
  }
);
FormMessage.displayName = 'FormMessage';

// Success message component for positive validation feedback
const FormSuccessMessage = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { formMessageId } = useFormField();

  if (!children) {
    return null;
  }

  return (
    <div
      ref={ref}
      id={formMessageId}
      className={cn(
        `flex items-start gap-2 ${TYPE_SCALE['2xs']} ${FONT_WEIGHTS.medium} ${RADIUS_SCALE.md} ${SIZE_SCALE['2']} ${COLOR_SCALE['success-bg']} ${COLOR_SCALE.success} ${INTERACTIVE_STATES.transitions.all} animate-in slide-in-from-top-1 duration-200`,
        className
      )}
      {...props}
    >
      <span className="flex-shrink-0 mt-0.5 text-xs" aria-hidden="true">
        ✓
      </span>
      <span>{children}</span>
    </div>
  );
});
FormSuccessMessage.displayName = 'FormSuccessMessage';

// Enhanced FormItem with validation state indicators
interface FormItemProps extends React.HTMLAttributes<HTMLDivElement> {
  showValidationState?: boolean;
}

const FormItemEnhanced = forwardRef<HTMLDivElement, FormItemProps>(
  ({ className, showValidationState = false, ...props }, ref) => {
    const id = useId();
    const { error } = useFormField();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div
          ref={ref}
          className={cn(
            `flex flex-col ${GAP_SCALE['2']} relative`,
            showValidationState &&
              error &&
              `ring-1 ring-destructive/20 rounded-lg p-3 bg-destructive/5`,
            showValidationState && !error && `ring-1 ring-success/20 rounded-lg p-3 bg-success/5`,
            className
          )}
          {...props}
        />
      </FormItemContext.Provider>
    );
  }
);
FormItemEnhanced.displayName = 'FormItemEnhanced';

export {
  Form,
  FormItem,
  FormItemEnhanced,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormSuccessMessage,
  FormField,
};
